'use client'

import { useState, useRef, type ReactNode } from 'react'
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion'
import { Tabs, TabsList, TabsTrigger, TabsContent, Button } from '@mdxui/primitives'
import ReactMarkdown from 'react-markdown'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import { CodeWindowBase } from '../code-window'
import {
  sectionVariants,
  headerVariants,
  reducedMotionVariants,
  ease,
} from '../../lib/motion'

/**
 * Props for FeaturesCodeTabs component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - This component requires 2-5 tabs for balanced layout
 * - Each tab needs a `label` and `title` at minimum
 * - Each tab can optionally have `description`, `bullets`, and `code`
 * - **Max 3 bullets recommended** for visual balance - more will crowd the content area
 * - Code windows extend to the right edge for visual balance
 * - Code window: auto height on mobile, fixed h-[350px] on desktop
 * - Code snippets should be ~8-15 lines to fit the fixed height
 * - Desktop: 50/50 split between content and code window
 * - Mobile: Tabs scroll horizontally, content stacks vertically
 * - Icons are hidden on mobile for responsive design
 * - Best used for showcasing different product features, SDK examples, or workflow steps
 */
export interface FeaturesCodeTabsProps {
  /** Section heading displayed above tabs */
  heading?: string
  /** Section description displayed below heading */
  description?: string
  /** Array of tab items (2-5 recommended) */
  tabs: {
    /** Tab label shown in the tab button */
    label: string
    /** Optional icon displayed before the label (hidden on mobile) */
    icon?: ReactNode
    /** Title shown in the content area when tab is active */
    title: string
    /** Description text for the tab content - supports Markdown (bold, code, links) */
    description: string
    /** Optional bullet points for additional details (max 3 recommended) - supports Markdown */
    bullets?: string[]
    /** Code block to display in the media area */
    code?: { code: string; language: string; filename?: string }
    /** Optional button displayed below bullets */
    button?: { text: string; url: string }
  }[]
  /** Additional className for the section */
  className?: string
}

// Content transition variants
const contentVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.2, ease },
  },
}

/**
 * FeaturesCodeTabs - Tab-based feature switcher with code windows per tab
 *
 * **Layout:**
 * ```
 * ┌──────────────────────────────────────────────┐
 * │           Heading & Description              │
 * ├──────────────────────────────────────────────┤
 * │     [Tab 1] [Tab 2] [Tab 3] [Tab 4]         │
 * ├──────────────────────────────────────────────┤
 * │ ┌────────────────────────────────────────┐  │
 * │ │                                        │  │
 * │ │  Title        │  ┌──────────────────┐  │  │
 * │ │  Description  │  │  [Code Window]   │  │  │
 * │ │  • Bullet 1   │  │                  │  │  │
 * │ │  • Bullet 2   │  └──────────────────┘  │  │
 * │ │               │                        │  │
 * │ └────────────────────────────────────────┘  │
 * └──────────────────────────────────────────────┘
 * ```
 *
 * **CONSTRAINTS:**
 * - Requires 2-5 tabs for balanced layout
 * - **Max 3 bullets per tab** for visual balance
 * - Code windows only (no image support)
 * - Desktop: 50/50 split between content and code window
 * - Code window extends to right edge (rounded left only)
 * - Mobile: Horizontal scrolling tabs, stacked content
 * - Icons hidden on mobile for responsive design
 *
 * **Best Use Cases:**
 * - Product feature showcase with code examples
 * - SDK/API documentation landing pages
 * - Multi-step workflow explanations
 * - Platform capability overviews
 *
 * @example
 * ```tsx
 * <FeaturesTabs
 *   heading="Platform Features"
 *   tabs={[
 *     {
 *       label: "Analytics",
 *       icon: <Zap className="size-4" />,
 *       title: "Real-time Analytics",
 *       description: "Track user behavior as it happens.",
 *       bullets: ["Live dashboards", "Custom metrics", "Instant alerts"], // max 3 recommended
 *       code: { code: "const data = await analytics.get()", language: "typescript" }
 *     },
 *     {
 *       label: "API",
 *       title: "Developer API",
 *       description: "Powerful REST API for integration.",
 *       code: { code: "fetch('/api/data')", language: "javascript" }
 *     }
 *   ]}
 * />
 * ```
 */
export function FeaturesCodeTabs({
  heading,
  description,
  tabs,
  className,
}: FeaturesCodeTabsProps) {
  const [activeTab, setActiveTab] = useState(tabs[0]?.label ?? '')
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const contentAnimation = prefersReducedMotion ? reducedMotionVariants : contentVariants

  if (!tabs || tabs.length === 0) {
    return null
  }

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 md:py-24 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        {/* Header */}
        {(heading || description) && (
          <motion.div
            className="mx-auto mb-12 max-w-3xl text-center"
            variants={headerAnimation}
          >
            {heading && (
              <h2 className="mb-4 text-3xl font-semibold tracking-tight lg:text-4xl">
                {heading}
              </h2>
            )}
            {description && (
              <Markdown size="lg">{description}</Markdown>
            )}
          </motion.div>
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex flex-col items-center"
        >
          {/* Horizontal scrolling tabs on mobile, centered on desktop */}
          <motion.div
            variants={headerAnimation}
            className="mb-8 w-full md:flex md:justify-center"
          >
            <div
              className="flex justify-center overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden md:overflow-visible md:pb-0"
              style={{
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE/Edge
              }}
            >
              <TabsList className="flex w-full md:inline-flex md:w-max md:flex-nowrap">
                {tabs.map((tab) => (
                  <TabsTrigger
                    key={tab.label}
                    value={tab.label}
                    className="flex-1 gap-2 whitespace-nowrap md:min-w-[4rem] md:flex-initial md:shrink-0"
                  >
                    {tab.icon && (
                      <span className="hidden sm:inline-block size-4">{tab.icon}</span>
                    )}
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </motion.div>

          {/* Content Area */}
          <div className="w-full">
            <AnimatePresence mode="popLayout">
              {tabs.map((tab) => (
                <TabsContent key={tab.label} value={tab.label} className="mt-0">
                  <motion.div
                    key={tab.label}
                    variants={contentAnimation}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="flex min-h-[400px] flex-col rounded-[var(--radius-2xl,calc(var(--radius)*2))] bg-muted md:min-h-[450px] md:flex-row"
                  >
                    {/* Text Content - 50% width on desktop */}
                    <div className="flex w-full flex-col justify-center p-6 md:w-1/2 md:p-10 lg:p-12">
                      <h3 className="mb-4 text-xl font-semibold md:text-2xl">
                        {tab.title}
                      </h3>
                      <div className="prose prose-sm mb-6 max-w-none leading-relaxed text-muted-foreground prose-p:m-0 prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-foreground/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                        <ReactMarkdown>{tab.description}</ReactMarkdown>
                      </div>
                      {tab.bullets && tab.bullets.length > 0 && (
                        <ul className="space-y-3">
                          {tab.bullets.map((bullet) => (
                            <li
                              key={bullet}
                              className="flex items-start gap-3 text-muted-foreground"
                            >
                              <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" />
                              <span className="prose prose-sm max-w-none prose-p:m-0 prose-p:inline prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-foreground/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none"><ReactMarkdown>{bullet}</ReactMarkdown></span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {tab.button && (
                        <Button
                          className="mt-6 w-full bg-foreground text-background hover:bg-foreground/90 md:w-fit"
                          asChild
                        >
                          <a href={tab.button.url}>{tab.button.text}</a>
                        </Button>
                      )}
                    </div>

                    {/* Code Window - 50% width, extends to right edge, vertically centered */}
                    {tab.code && (
                      <div className="flex w-full items-center py-4 pl-4 md:w-1/2 md:py-8 md:pl-8">
                        <CodeWindowBase
                          code={tab.code.code}
                          language={tab.code.language}
                          filename={tab.code.filename}
                          showHeader={false}
                          showCopy={false}
                          theme="auto"
                          style="borderless"
                          className="h-auto w-full rounded-l-[var(--radius-xl,calc(var(--radius)*1.5))] md:h-[350px]"
                        />
                      </div>
                    )}
                  </motion.div>
                </TabsContent>
              ))}
            </AnimatePresence>
          </div>
        </Tabs>
      </div>
    </motion.section>
  )
}
